import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import AdminLayout from "../components/AdminLayout";
import { Button } from "../components/ui/Button";
import { Input, Label } from "../components/ui/Input";
import { api, extractError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  Plus,
  PencilSimple,
  Copy as CopySimple,
  Trash,
  MagnifyingGlass,
  X,
  Warning,
  BookOpenText,
  CheckCircle,
  Info,
  UploadSimple,
  Sparkle,
  FileText,
  Table as TableIcon,
  Eye,
  EyeSlash,
  UsersThree,
  Lock,
} from "@phosphor-icons/react";
import { getEquivalenceTerms } from "./tool/shared";

const EMPTY_FORM = {
  name: "",
  kuerzel: "",
  short_description: "",
  ruleset: "",
  vordienst_anrechnungsregeln: "",
  gehaltsstufen_tabelle: [],
  equivalence_check_required: false,
  is_visible: true,
  beta_user_ids: [],
  // Iter 19.66.2 — KV-spezifische Cap-Bypass-Sonderregeln (Phase 1 zog die UI
  // vom strukturierten Editor in diesen "einfachen" Editor, weil der Admin
  // hier arbeitet, nicht dort).
  cap_bypass_sonderregeln: [],
  // Iter 20.21 — Admin-editierbarer Info-Hinweis (markdown-fähig), keine
  // Logik. Frontend zeigt ihn in Schritt 3 im KV-Sonderregeln-Card.
  zusatz_hinweis: "",
  // Iter 20.33 — Native KV-Reihenfolge der Beschäftigungsgruppen (kommasep).
  gruppen_order: "",
  // Iter 20.36 — KV-spezifisches Wording „Gleichwertigkeit" vs „Facheinschlägigkeit".
  equivalence_terminology: "gleichwertigkeit",
};

// ── Iter 19.66.2 + Iter 20.12 — KV-Sonderregel-Templates ─────────────────────
// Diskrete Liste der unterstützten Sonderregel-Typen. UI rendert pro Typ
// nur die relevanten Eingabefelder. MUSS synchron mit `_valid_typ` im
// Backend (`core/schemas.py`) und mit `CAP_BYPASS_TYPES` im
// `AdminKvStructuredEditor.jsx` bleiben.
//
// Gruppierung (Iter 20.12):
//   • selber_ag    — Selber-Arbeitgeber-Ausnahmen (3 Cap-Bypass-Varianten)
//   • zeitfenster  — Anrechnungs-Zeitfenster (nur letzte N Jahre relevant)
const CAP_BYPASS_TYPES = [
  {
    typ: "selber_arbeitgeber_lueckenregel",
    group: "selber_ag",
    title: "Selber AG · Lückenregel",
    description:
      "Cap-Bypass beim selben Arbeitgeber gilt nur, wenn die zeitliche Lücke zur aktuellen Anstellung ≤ X Monate beträgt (z. B. KV Handel Arbeiter: 24 Monate).",
    defaults: { max_lueckendauer_monate: 24, faktor: 1.0, max_jahre: 0, zeitfenster_jahre: 0 },
    fields: ["max_lueckendauer_monate"],
  },
  {
    typ: "selber_arbeitgeber_vorarbeiterzeit",
    group: "selber_ag",
    title: "Selber AG · Vorarbeiterzeit",
    description:
      "Vorarbeiterzeiten beim selben Arbeitgeber zählen mit reduziertem Faktor (z. B. KV Gewerbe: 50 %, max. 5 Jahre).",
    defaults: { max_lueckendauer_monate: 0, faktor: 0.5, max_jahre: 5, zeitfenster_jahre: 0 },
    fields: ["faktor", "max_jahre"],
  },
  {
    typ: "selber_arbeitgeber_gleiche_gruppe",
    group: "selber_ag",
    title: "Selber AG · Gleiche/Höhere Gruppe",
    description:
      "Cap-Bypass nur, wenn die Vordienst-Position die gleiche oder eine höhere Verwendungsgruppe hatte (KV Gewerbe).",
    defaults: { max_lueckendauer_monate: 0, faktor: 1.0, max_jahre: 0, zeitfenster_jahre: 0 },
    fields: [],
  },
  {
    typ: "zeitfenster_relevanz",
    group: "zeitfenster",
    title: "Zeitfenster · Nur letzte N Jahre",
    description:
      "Nur Vordienstzeiten der letzten X Jahre vor dem Stichtag werden angerechnet. Ältere Zeiten werden ausgeschlossen; teilweise ins Fenster reichende Zeiträume werden am Fenster-Anfang abgeschnitten.",
    defaults: { max_lueckendauer_monate: 0, faktor: 1.0, max_jahre: 0, zeitfenster_jahre: 7 },
    fields: ["zeitfenster_jahre"],
  },
  // Iter 20.36.f2 — WT-Kanzlei-Kombiregel (Steuerberater-/WT-KV).
  // Iter 20.36.f5 — Regel wirkt jetzt auf die Math-Engine (siehe Backend
  // `_apply_wt_kanzlei_faktor_reduktion` + `_apply_wt_kanzlei_sub_cap`).
  //  • `max_jahre_wt` (10): Global-Cap in Jahren, ersetzt KV-Standard-Cap.
  //  • `max_jahre_non_wt` (5): Sub-Cap für Non-WT-Zeilen (effektive Monate).
  //  • `faktor_non_wt` (0.5): Anrechnungsfaktor für Non-WT-Zeilen.
  {
    typ: "wt_kanzlei_kombiregel",
    group: "wt",
    title: "WT-Kanzlei · Kombiregel",
    description:
      "Zwei-Töpfe-Anrechnung mit Global-Cap. WT-Kanzlei-Zeiten (Checkbox in Schritt 3) zählen voll bis zur Gesamt-Höchstanrechnungsdauer (max_jahre_wt, typ. 10 J.). Zeiten außerhalb einer WT-Kanzlei zählen halbiert (faktor_non_wt, typ. 0.5), begrenzt auf max_jahre_non_wt (typ. 5 J.) effektiv anrechenbar. Global-Cap darf nicht überschritten werden.",
    defaults: {
      max_lueckendauer_monate: 0,
      faktor: 1.0,
      max_jahre: 0,
      zeitfenster_jahre: 0,
      max_jahre_wt: 10,
      max_jahre_non_wt: 5,
      faktor_non_wt: 0.5,
    },
    fields: ["max_jahre_wt", "max_jahre_non_wt", "faktor_non_wt"],
    field_labels: {
      max_jahre_wt: "Gesamt-Höchstanrechnungsdauer (Jahre)",
      max_jahre_non_wt: "Max. Jahre Nicht-WT (halbiert)",
      faktor_non_wt: "Anrechnungsfaktor Nicht-WT (0.0 — 1.0)",
    },
    field_helpers: {
      max_jahre_wt:
        "Ersetzt den KV-Standard-Cap. Bei aktiver Regel dürfen alle anrechenbaren Zeilen zusammen diesen Wert nicht überschreiten.",
      max_jahre_non_wt:
        'Effektive Höchstanrechnung für Zeilen OHNE "In WT-Kanzlei erbracht"-Häkchen (nach Faktor-Reduktion).',
      faktor_non_wt:
        "Reduktions-Faktor für alle Non-WT-Zeilen. Typ. 0.5 = halbieren.",
    },
  },
  // Iter 20.36.f3 — Altersfaktor-Regel (deterministisch, mit Math-Engine).
  {
    typ: "facheinschlaegig_altersfaktor_regel",
    group: "altersfaktor",
    title: "Altersfaktor · Zeitwertige Anrechnung",
    description:
      "Halbiert den Anrechnungsfaktor (typisch faktor=0.5) für anrechenbare Vordienstzeiten, die vor dem Zeitfenster (typisch zeitfenster_jahre=10) enden. Ausnahme: Bei mindestens max_jahre (typisch 5) anrechenbaren Jahren im Zeitfenster greift die Reduktion NICHT — Person hat aktuelle Berufserfahrung.",
    defaults: { max_lueckendauer_monate: 0, faktor: 0.5, max_jahre: 5, zeitfenster_jahre: 10 },
    fields: ["zeitfenster_jahre", "max_jahre", "faktor"],
  },
  // Iter 20.36.f9 — SV-Status-Höchstanrechnung nach Kategorie (z.B. KBG
  // max. 24 Monate im KV Steuerberater/Wirtschaftsprüfer). Wirkt
  // deterministisch auf die Math-Engine (`_apply_sv_status_max_dauer`):
  // erkennt anhand von Tätigkeit/Begründung die Kategorie und deckelt
  // chronologisch die anrechenbaren Monate.
  {
    typ: "sv_status_max_dauer",
    group: "sv_status",
    title: "SV-Status · Höchstanrechnung nach Kategorie",
    description:
      "Deckelt Zeiten einer bestimmten SV-Status-Kategorie (z. B. Kinderbetreuungsgeld, Karenz, Präsenzdienst) auf eine maximale Anrechnungsdauer in Monaten. Zeilen der gewählten Kategorie werden chronologisch bis zur Grenze summiert; Überschuss wird partiell reduziert oder auf 0 gesetzt.",
    defaults: {
      max_lueckendauer_monate: 0,
      faktor: 1.0,
      max_jahre: 0,
      zeitfenster_jahre: 0,
      sv_status_kategorie: "kbg",
      max_monate: 24,
    },
    fields: ["sv_status_kategorie", "max_monate"],
    field_labels: {
      sv_status_kategorie: "Kategorie",
      max_monate: "Max. anrechenbare Monate",
    },
    field_helpers: {
      sv_status_kategorie:
        "Welche SV-Status-Art soll gedeckelt werden. Erkennung erfolgt anhand der Tätigkeit/Begründung.",
      max_monate:
        "Höchstdauer in Kalender-Monaten. Zeilen der Kategorie werden chronologisch bis zu diesem Wert angerechnet.",
    },
    field_options: {
      sv_status_kategorie: [
        { value: "kbg", label: "Kinderbetreuungsgeld (KBG)" },
        { value: "karenz", label: "Karenz (Eltern-, Väter-, Bildungs-, Pflege-)" },
        { value: "praesenzdienst", label: "Präsenz-/Zivildienst" },
        { value: "wochengeld", label: "Wochengeld / Mutterschutz" },
        { value: "krankengeld", label: "Krankengeld" },
        { value: "arbeitslosengeld", label: "Arbeitslosengeld / AMS" },
      ],
    },
  },
  // Iter 20.36.f14.12 — Präsenzdienst/Zivildienst-Sonderregel. Steuert
  // das UI-Verhalten für Präsenz-/Zivildienst-Zeilen in Schritt 3:
  //  • `gesetzlich` (Default): Zeile ist anrechenbar=true, aber
  //    Warn-Balken „Umschließung durch DV prüfen" wird angezeigt.
  //  • `immer_anrechenbar` (z.B. KV Handel Angestellte): Zeile ist
  //    anrechenbar=true ohne Warn-Balken.
  // Wirkt deterministisch in der Math-Engine (`_apply_praesenzdienst_regel`).
  {
    typ: "praesenzdienst_regel",
    group: "praesenzdienst",
    title: "Präsenz-/Zivildienst · Anrechnungsverhalten",
    description:
      "Legt fest, wie Präsenz-/Zivildienst-Zeiten initial angerechnet werden. Modus 'gesetzlich' (Default) markiert die Zeile mit einem gelben Prüf-Balken, damit der Sachbearbeiter die gesetzliche Umschließungspflicht durch ein Dienst- oder Lehrverhältnis prüft. Modus 'immer_anrechenbar' rechnet Präsenz-/Zivildienst ohne Warnung voll an (z. B. KV Handel Angestellte).",
    defaults: {
      max_lueckendauer_monate: 0,
      faktor: 1.0,
      max_jahre: 0,
      zeitfenster_jahre: 0,
      praesenzdienst_modus: "gesetzlich",
    },
    fields: ["praesenzdienst_modus"],
    field_labels: {
      praesenzdienst_modus: "Modus",
    },
    field_helpers: {
      praesenzdienst_modus:
        "'gesetzlich' → Warn-Balken (Umschließung prüfen). 'immer_anrechenbar' → ohne Warnung voll anrechenbar.",
    },
    field_options: {
      praesenzdienst_modus: [
        { value: "gesetzlich", label: "Gesetzlich (Warn-Balken: Umschließung prüfen)" },
        { value: "immer_anrechenbar", label: "Immer anrechenbar (ohne Warnung)" },
      ],
    },
  },
];

// Gruppen-Metadaten für UI-Überschriften im „Neu hinzufügen"-Bereich.
const CAP_BYPASS_GROUPS = [
  { id: "selber_ag", title: "Selber-Arbeitgeber" },
  { id: "zeitfenster", title: "Zeitfenster" },
  // Iter 20.36.f2 — Wirtschaftstreuhand / Steuerberater-KV-Sonderregeln.
  { id: "wt", title: "Wirtschaftstreuhand" },
  // Iter 20.36.f3 — Altersfaktor: zeitwertige Anrechnung.
  { id: "altersfaktor", title: "Altersfaktor" },
  // Iter 20.36.f9 — SV-Status-Höchstanrechnung (KBG, Karenz, …).
  { id: "sv_status", title: "SV-Status-Höchstanrechnung" },
  // Iter 20.36.f14.12 — Präsenz-/Zivildienst-Sonderregel.
  { id: "praesenzdienst", title: "Präsenz-/Zivildienst" },
];

function emptyCapBypass(typ) {
  const tpl = CAP_BYPASS_TYPES.find((t) => t.typ === typ) || CAP_BYPASS_TYPES[0];
  return {
    typ: tpl.typ,
    max_lueckendauer_monate: tpl.defaults.max_lueckendauer_monate,
    faktor: tpl.defaults.faktor,
    max_jahre: tpl.defaults.max_jahre,
    zeitfenster_jahre: tpl.defaults.zeitfenster_jahre || 0,
    // Iter 20.36.f5 — WT-Kanzlei-Kombiregel-Parameter (Backend-Schema erlaubt
    // sie generell, andere Regeln lassen sie auf 0/1.0).
    max_jahre_wt: tpl.defaults.max_jahre_wt || 0,
    max_jahre_non_wt: tpl.defaults.max_jahre_non_wt || 0,
    faktor_non_wt:
      typeof tpl.defaults.faktor_non_wt === "number"
        ? tpl.defaults.faktor_non_wt
        : 1.0,
    // Iter 20.36.f9 — SV-Status-Höchstanrechnungs-Parameter.
    sv_status_kategorie: tpl.defaults.sv_status_kategorie || "",
    max_monate: tpl.defaults.max_monate || 0,
    // Iter 20.36.f14.12 — Präsenzdienst-Regel-Modus.
    praesenzdienst_modus: tpl.defaults.praesenzdienst_modus || "",
    label_de: tpl.description,
    aktiv: true,
  };
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("de-AT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function GehaltsstufenEditor({ rows, onChange }) {
  const setRow = (idx, key, value) => {
    onChange(rows.map((r, i) => (i === idx ? { ...r, [key]: value } : r)));
  };
  const addRow = () => {
    onChange([...rows, { gruppe: "", stufe: "", jahre: "" }]);
  };
  const removeRow = (idx) => {
    onChange(rows.filter((_, i) => i !== idx));
  };
  const clearAll = () => onChange([]);

  return (
    <div data-testid="kv-gehaltsstufen-editor" className="space-y-2">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Label className="mb-0 flex items-center gap-2">
          <TableIcon size={14} weight="bold" className="text-warm-500" />
          Gehaltsstufen-Tabelle
          <span className="ml-2 text-[10px] tracking-normal text-warm-400 normal-case">
            (Gruppe × Stufe × Jahres-Spanne — keine Gehaltsbeträge)
          </span>
        </Label>
        <div className="text-[11px] text-warm-500" data-testid="kv-gehaltsstufen-count">
          {rows.length} {rows.length === 1 ? "Zeile" : "Zeilen"}
        </div>
      </div>

      {rows.length === 0 ? (
        <div
          data-testid="kv-gehaltsstufen-empty"
          className="border border-dashed border-warm-300 bg-warm-50 rounded-sm px-4 py-5 text-center text-[12px] text-warm-600"
        >
          Noch keine Gehaltsstufen erfasst.
          <br />
          Beim KI-Upload werden sie automatisch vorgeschlagen — oder fügen Sie unten manuell Zeilen hinzu.
        </div>
      ) : (
        <div className="border border-ink-200 rounded-sm overflow-hidden">
          <div className="grid grid-cols-12 gap-0 bg-ink-50 text-[10px] uppercase tracking-widest font-semibold text-warm-500 border-b border-ink-200">
            <div className="col-span-4 px-3 py-2">Beschäftigungsgruppe</div>
            <div className="col-span-3 px-3 py-2 border-l border-ink-200">Stufe</div>
            <div className="col-span-4 px-3 py-2 border-l border-ink-200">Jahres-Spanne</div>
            <div className="col-span-1 px-3 py-2 border-l border-ink-200" aria-label="Aktionen" />
          </div>
          <ul className="divide-y divide-ink-100">
            {rows.map((row, idx) => (
              <li
                key={idx}
                data-testid={`kv-gehaltsstufen-row-${idx}`}
                className="grid grid-cols-12 gap-0 hover:bg-warm-50/50 transition-crisp"
              >
                <div className="col-span-4">
                  <input
                    type="text"
                    value={row.gruppe}
                    onChange={(e) => setRow(idx, "gruppe", e.target.value)}
                    placeholder="z.B. A"
                    maxLength={40}
                    data-testid={`kv-gehaltsstufen-row-${idx}-gruppe`}
                    className="w-full px-3 py-2 text-sm text-ink-900 placeholder:text-warm-400 bg-transparent border-0 focus:bg-brand-50 focus:outline-none transition-crisp"
                  />
                </div>
                <div className="col-span-3 border-l border-ink-100">
                  <input
                    type="text"
                    value={row.stufe}
                    onChange={(e) => setRow(idx, "stufe", e.target.value)}
                    placeholder="z.B. 1"
                    maxLength={40}
                    data-testid={`kv-gehaltsstufen-row-${idx}-stufe`}
                    className="w-full px-3 py-2 text-sm text-ink-900 placeholder:text-warm-400 bg-transparent border-0 focus:bg-brand-50 focus:outline-none transition-crisp"
                  />
                </div>
                <div className="col-span-4 border-l border-ink-100">
                  <input
                    type="text"
                    value={row.jahre}
                    onChange={(e) => setRow(idx, "jahre", e.target.value)}
                    placeholder="z.B. 0-2"
                    maxLength={40}
                    data-testid={`kv-gehaltsstufen-row-${idx}-jahre`}
                    className="w-full px-3 py-2 text-sm text-ink-900 placeholder:text-warm-400 bg-transparent border-0 focus:bg-brand-50 focus:outline-none transition-crisp"
                  />
                </div>
                <div className="col-span-1 border-l border-ink-100 flex items-center justify-center">
                  <button
                    type="button"
                    onClick={() => removeRow(idx)}
                    aria-label="Zeile entfernen"
                    data-testid={`kv-gehaltsstufen-row-${idx}-remove`}
                    className="p-2 text-warm-400 hover:text-status-abgelehnt-text transition-crisp"
                  >
                    <Trash size={13} weight="bold" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mt-2">
        <button
          type="button"
          onClick={addRow}
          data-testid="kv-gehaltsstufen-add"
          className="inline-flex items-center gap-2 border border-ink-300 bg-white px-3 py-1.5 rounded-sm text-[12px] font-medium text-ink-700 hover:bg-ink-100 transition-crisp"
        >
          <Plus size={12} weight="bold" />
          Zeile hinzufügen
        </button>
        {rows.length > 0 && (
          <button
            type="button"
            onClick={clearAll}
            data-testid="kv-gehaltsstufen-clear"
            className="inline-flex items-center gap-1.5 text-[11px] text-warm-500 hover:text-status-abgelehnt-text transition-crisp"
          >
            <X size={11} weight="bold" />
            Alle Zeilen entfernen
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Compute a line-level diff between two texts.
 * Returns an array of {type: 'same'|'add'|'del', text} ops via a simple
 * LCS (Longest Common Subsequence) traversal — small inputs, no need for
 * a 3rd-party lib.
 */
function diffLines(oldText, newText) {
  const a = (oldText || "").split("\n");
  const b = (newText || "").split("\n");
  const n = a.length;
  const m = b.length;
  // LCS DP table.
  const dp = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      if (a[i] === b[j]) dp[i][j] = dp[i + 1][j + 1] + 1;
      else dp[i][j] = Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const ops = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      ops.push({ type: "same", text: a[i] });
      i++;
      j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      ops.push({ type: "del", text: a[i] });
      i++;
    } else {
      ops.push({ type: "add", text: b[j] });
      j++;
    }
  }
  while (i < n) {
    ops.push({ type: "del", text: a[i++] });
  }
  while (j < m) {
    ops.push({ type: "add", text: b[j++] });
  }
  return ops;
}


function VordienstReExtractDialog({ kvId, currentText, onClose, onApply }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null); // {old_text, new_text, excel_filename, …}

  const handleSelect = (e) => {
    const f = e.target.files?.[0] || null;
    setError("");
    setResult(null);
    if (!f) {
      setFile(null);
      return;
    }
    const ok = /\.(xlsx|xls|xlsm)$/i.test(f.name);
    if (!ok) {
      setError("Bitte eine Excel-Datei (.xlsx, .xls oder .xlsm) auswählen.");
      setFile(null);
      return;
    }
    setFile(f);
  };

  const handleExtract = async () => {
    if (!file || !kvId) return;
    setBusy(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("excel_file", file);
      const { data } = await api.post(
        `/admin/kvs/${encodeURIComponent(kvId)}/re-extract-vordienst`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 }
      );
      setResult(data);
    } catch (err) {
      setError(extractError(err, "Automatisches Auslesen fehlgeschlagen."));
    } finally {
      setBusy(false);
    }
  };

  const ops = useMemo(
    () => (result ? diffLines(result.old_text || currentText, result.new_text) : []),
    [result, currentText]
  );
  const addedCount = ops.filter((o) => o.type === "add").length;
  const removedCount = ops.filter((o) => o.type === "del").length;
  const unchanged = ops.filter((o) => o.type === "same").length;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-ink-900/60 px-4"
      role="dialog"
      aria-modal="true"
      data-testid="vordienst-reextract-dialog"
    >
      <div className="bg-white border border-ink-200 rounded-sm w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="px-6 py-4 border-b border-ink-200 flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-semibold text-warm-500 mb-1 inline-flex items-center gap-1.5">
              <Sparkle size={11} weight="bold" />
              KI-Re-Extract · Anrechnungsregeln
            </div>
            <h2 className="font-cabinet text-xl tracking-tight text-ink-900 font-medium leading-tight">
              Vordienst-Regeln aus korrigiertem Excel neu auslesen
            </h2>
            <p className="text-xs text-warm-600 mt-1 max-w-xl">
              Es wird ausschließlich der Bereich &bdquo;Spezifische Anrechnungsregeln&ldquo;
              neu erfasst. Alle anderen KV-Stammdaten bleiben unverändert.
            </p>
          </div>
          <button
            onClick={onClose}
            className="inline-flex items-center justify-center h-8 w-8 rounded-sm text-warm-500 hover:bg-ink-100 transition-crisp"
            aria-label="Schließen"
            data-testid="vordienst-reextract-close"
          >
            <X size={14} weight="bold" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {!result && (
            <>
              <label className="block">
                <span className="text-xs font-semibold text-ink-900 uppercase tracking-widest mb-2 block">
                  Korrigiertes Excel auswählen
                </span>
                <input
                  type="file"
                  accept=".xlsx,.xls,.xlsm,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  onChange={handleSelect}
                  disabled={busy}
                  data-testid="vordienst-reextract-file"
                  className="w-full text-sm file:mr-3 file:py-2 file:px-3 file:rounded-sm file:border file:border-ink-300 file:bg-white file:text-ink-700 file:cursor-pointer file:hover:bg-ink-100 file:font-medium"
                />
                {file && (
                  <span
                    className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-ink-700"
                    data-testid="vordienst-reextract-file-info"
                  >
                    <FileText size={11} weight="bold" />
                    {file.name} · {(file.size / 1024).toFixed(1)} KB
                  </span>
                )}
              </label>

              {error && (
                <div
                  className="border border-status-abgelehnt-border bg-status-abgelehnt-bg rounded-sm p-3 text-sm text-status-abgelehnt-text"
                  data-testid="vordienst-reextract-error"
                >
                  {error}
                </div>
              )}
            </>
          )}

          {busy && (
            <div className="border border-ink-200 bg-warm-50 rounded-sm p-6 text-center">
              <div className="inline-flex items-center gap-2 text-sm text-ink-700">
                <span className="brand-dot" />
                <span className="brand-dot" />
                <span className="brand-dot" />
                <span className="ml-2">ET liest Excel und extrahiert Anrechnungsregeln …</span>
              </div>
            </div>
          )}

          {result && (
            <>
              <div className="flex items-center gap-3 flex-wrap text-[11px]">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-emerald-50 text-emerald-700 border border-emerald-200 font-semibold uppercase tracking-wider">
                  +{addedCount} neu
                </span>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-red-50 text-red-700 border border-red-200 font-semibold uppercase tracking-wider">
                  −{removedCount} entfernt
                </span>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-ink-100 text-warm-700 border border-ink-200 font-semibold uppercase tracking-wider">
                  {unchanged} unverändert
                </span>
                {result.excel_truncated && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-amber-50 text-amber-700 border border-amber-200 font-medium">
                    Excel wurde gekürzt (zu groß)
                  </span>
                )}
              </div>

              <div className="border border-ink-200 rounded-sm bg-white font-mono text-[12px] leading-relaxed max-h-[50vh] overflow-y-auto">
                {ops.length === 0 && (
                  <div className="px-3 py-4 text-warm-500 text-xs">
                    Keine Unterschiede erkannt — der bestehende Text stimmt mit der KI-Antwort überein.
                  </div>
                )}
                {ops.map((op, idx) => (
                  <div
                    key={idx}
                    className={
                      op.type === "add"
                        ? "px-3 py-0.5 bg-emerald-50 border-l-2 border-emerald-400 text-emerald-900"
                        : op.type === "del"
                        ? "px-3 py-0.5 bg-red-50 border-l-2 border-red-400 text-red-900 line-through decoration-red-400/60"
                        : "px-3 py-0.5 text-warm-600 border-l-2 border-transparent"
                    }
                    data-testid={`vordienst-diff-${op.type}-${idx}`}
                  >
                    <span
                      className={`inline-block w-4 mr-2 font-bold ${
                        op.type === "add"
                          ? "text-emerald-700"
                          : op.type === "del"
                          ? "text-red-700"
                          : "text-warm-300"
                      }`}
                    >
                      {op.type === "add" ? "+" : op.type === "del" ? "−" : " "}
                    </span>
                    {op.text || "\u00A0"}
                  </div>
                ))}
              </div>

              <p className="text-[11px] text-warm-500 leading-relaxed">
                <strong className="text-ink-900">Hinweis:</strong>{" "}
                Beim Übernehmen wird der gesamte Regeltext durch die KI-Version ersetzt.
                Möchten Sie nur einzelne Zeilen übernehmen, kopieren Sie diese
                bitte manuell ins Formular und schließen Sie den Dialog ohne Übernahme.
              </p>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-ink-200 bg-ink-50 flex items-center justify-end gap-3 flex-wrap">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            data-testid="vordienst-reextract-cancel"
            className="inline-flex items-center gap-2 rounded-sm border border-ink-300 bg-white px-3.5 py-2 text-xs font-medium text-ink-700 transition-crisp hover:bg-ink-100 disabled:opacity-50"
          >
            Abbrechen
          </button>
          {!result && (
            <button
              type="button"
              onClick={handleExtract}
              disabled={!file || busy}
              data-testid="vordienst-reextract-run"
              className="inline-flex items-center gap-2 rounded-sm bg-brand-400 px-3.5 py-2 text-xs font-semibold text-white transition-crisp hover:bg-brand-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Sparkle size={12} weight="bold" />
              {busy ? "KI analysiert…" : "Mit KI auslesen"}
            </button>
          )}
          {result && (
            <>
              <button
                type="button"
                onClick={() => {
                  setResult(null);
                  setFile(null);
                }}
                disabled={busy}
                data-testid="vordienst-reextract-redo"
                className="inline-flex items-center gap-2 rounded-sm border border-ink-300 bg-white px-3.5 py-2 text-xs font-medium text-ink-700 transition-crisp hover:bg-ink-100"
              >
                Anderes Excel
              </button>
              <button
                type="button"
                onClick={() => onApply(result.new_text)}
                data-testid="vordienst-reextract-apply"
                className="inline-flex items-center gap-2 rounded-sm bg-ink-900 px-3.5 py-2 text-xs font-semibold text-white transition-crisp hover:bg-ink-800"
              >
                <CheckCircle size={12} weight="bold" />
                Neue Regeln übernehmen
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


// ── Iter 19.66.2 — Cap-Bypass-Sonderregeln-Editor (im einfachen Editor) ────
// Collapsible-Sektion in der KV-Modal. Pro Eintrag dynamisch nur die
// typ-relevanten Felder. Wiederverwendet die `CAP_BYPASS_TYPES`-Definition
// am Modulanfang.
function CapBypassSonderregelnSection({ value, onChange }) {
  const rules = Array.isArray(value) ? value : [];
  const [open, setOpen] = useState(rules.length > 0);

  const updateRule = (idx, patch) => {
    onChange(rules.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };
  const removeRule = (idx) => {
    onChange(rules.filter((_, i) => i !== idx));
  };
  const addRule = (typ) => {
    onChange([...rules, emptyCapBypass(typ)]);
  };

  return (
    <details
      open={open}
      onToggle={(e) => setOpen(e.currentTarget.open)}
      className="rounded-sm border border-ink-200 bg-warm-50/40"
      data-testid="kv-cap-bypass-section"
    >
      <summary className="cursor-pointer px-3 py-3 font-medium text-ink-900 hover:bg-warm-50 transition-crisp select-none flex items-center gap-2">
        <Info size={13} weight="bold" className="text-brand-500 shrink-0" />
        <span className="flex-1">
          Sonderregeln{" "}
          <span className="text-xs font-normal text-warm-600">
            ({rules.length} aktiv)
          </span>
        </span>
        <span className="text-xs text-warm-500 font-normal">
          {open ? "Einklappen" : "Aufklappen"}
        </span>
      </summary>

      <div className="px-3 pb-3 pt-2 space-y-3">
        <p className="text-xs text-warm-700 leading-relaxed">
          Hier definierst du KV-spezifische Ausnahmen und Sonderfälle bei
          der Anrechnung von Vordienstzeiten — z.&nbsp;B. bevorzugte
          Behandlung beim selben Arbeitgeber oder ein zeitliches
          Relevanzfenster. Jede Regel wirkt zusätzlich zu den
          Standard-Anrechnungsregeln dieses KV.
        </p>

        {rules.length === 0 && (
          <div
            className="text-xs italic text-warm-600 py-2"
            data-testid="kv-cap-bypass-empty"
          >
            Keine Sonderregeln aktiv — es gelten die Standard-Anrechnungsregeln
            (voller Faktor bis zur Höchstanrechnungsdauer).
          </div>
        )}

        {rules.map((rule, idx) => {
          const meta =
            CAP_BYPASS_TYPES.find((t) => t.typ === rule.typ) || CAP_BYPASS_TYPES[0];
          return (
            <div
              key={`${rule.typ}-${idx}`}
              className="border border-ink-200 bg-white rounded-sm p-3 space-y-2"
              data-testid={`kv-cap-bypass-row-${idx}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] uppercase tracking-widest font-bold text-brand-600 mb-0.5">
                    {meta.title}
                  </div>
                  <div className="text-xs text-warm-600 leading-relaxed">
                    {meta.description}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => removeRule(idx)}
                  className="shrink-0 inline-flex h-7 w-7 items-center justify-center rounded-sm border border-ink-200 text-warm-500 hover:text-red-600 hover:border-red-300 transition-crisp"
                  data-testid={`kv-cap-bypass-remove-${idx}`}
                  title="Sonderregel entfernen"
                >
                  <Trash size={13} weight="bold" />
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {meta.fields.includes("max_lueckendauer_monate") && (
                  <label className="block text-xs">
                    <span className="block text-warm-600 font-semibold mb-0.5">
                      Max. Lücke (Monate)
                    </span>
                    <input
                      type="number"
                      min={1}
                      max={240}
                      value={rule.max_lueckendauer_monate || 0}
                      onChange={(e) =>
                        updateRule(idx, {
                          max_lueckendauer_monate: Number(e.target.value) || 0,
                        })
                      }
                      data-testid={`kv-cap-bypass-lueckendauer-${idx}`}
                      className="w-full rounded-sm border border-ink-300 bg-white px-2 py-1.5 text-sm focus:border-brand-400 focus:outline-none focus-visible:ring-1 focus-visible:ring-brand-400"
                    />
                  </label>
                )}
                {meta.fields.includes("faktor") && (
                  <label className="block text-xs">
                    <span className="block text-warm-600 font-semibold mb-0.5">
                      Faktor (0.0 – 1.0)
                    </span>
                    <input
                      type="number"
                      step="0.05"
                      min={0}
                      max={1}
                      value={rule.faktor ?? 0.5}
                      onChange={(e) =>
                        updateRule(idx, { faktor: Number(e.target.value) || 0 })
                      }
                      data-testid={`kv-cap-bypass-faktor-${idx}`}
                      className="w-full rounded-sm border border-ink-300 bg-white px-2 py-1.5 text-sm focus:border-brand-400 focus:outline-none focus-visible:ring-1 focus-visible:ring-brand-400"
                    />
                  </label>
                )}
                {meta.fields.includes("max_jahre") && (
                  <label className="block text-xs">
                    <span className="block text-warm-600 font-semibold mb-0.5">
                      Max. Anrechnung (Jahre)
                    </span>
                    <input
                      type="number"
                      min={0}
                      max={50}
                      value={rule.max_jahre || 0}
                      onChange={(e) =>
                        updateRule(idx, { max_jahre: Number(e.target.value) || 0 })
                      }
                      data-testid={`kv-cap-bypass-maxjahre-${idx}`}
                      className="w-full rounded-sm border border-ink-300 bg-white px-2 py-1.5 text-sm focus:border-brand-400 focus:outline-none focus-visible:ring-1 focus-visible:ring-brand-400"
                    />
                  </label>
                )}
                {meta.fields.includes("zeitfenster_jahre") && (
                  <label className="block text-xs sm:col-span-2">
                    <span className="block text-warm-600 font-semibold mb-0.5">
                      Zeitfenster (Jahre vor Stichtag)
                    </span>
                    <input
                      type="number"
                      min={1}
                      max={50}
                      value={rule.zeitfenster_jahre || 0}
                      onChange={(e) =>
                        updateRule(idx, { zeitfenster_jahre: Number(e.target.value) || 0 })
                      }
                      data-testid={`kv-cap-bypass-zeitfenster-${idx}`}
                      className="w-full max-w-[160px] rounded-sm border border-ink-300 bg-white px-2 py-1.5 text-sm focus:border-brand-400 focus:outline-none focus-visible:ring-1 focus-visible:ring-brand-400"
                    />
                    <span className="mt-1 block text-[10.5px] text-warm-500 leading-relaxed">
                      Vordienstzeiten, die vollständig vor dem Stichtag − N Jahre
                      liegen, werden ausgeschlossen. Teilweise ins Fenster
                      reichende Zeiträume werden am Fenster-Anfang abgeschnitten.
                    </span>
                  </label>
                )}
                {/* Iter 20.36.f5 — WT-Kanzlei-Kombiregel-Felder. */}
                {meta.fields.includes("max_jahre_wt") && (
                  <label className="block text-xs">
                    <span className="block text-warm-600 font-semibold mb-0.5">
                      {meta.field_labels?.max_jahre_wt || "Max. Jahre WT-Kanzlei"}
                    </span>
                    <input
                      type="number"
                      min={0}
                      max={50}
                      value={rule.max_jahre_wt || 0}
                      onChange={(e) =>
                        updateRule(idx, {
                          max_jahre_wt: Number(e.target.value) || 0,
                        })
                      }
                      data-testid={`kv-cap-bypass-maxjahrewt-${idx}`}
                      className="w-full rounded-sm border border-ink-300 bg-white px-2 py-1.5 text-sm focus:border-brand-400 focus:outline-none focus-visible:ring-1 focus-visible:ring-brand-400"
                    />
                    {meta.field_helpers?.max_jahre_wt && (
                      <span className="mt-1 block text-[10.5px] text-warm-500 leading-relaxed">
                        {meta.field_helpers.max_jahre_wt}
                      </span>
                    )}
                  </label>
                )}
                {meta.fields.includes("max_jahre_non_wt") && (
                  <label className="block text-xs">
                    <span className="block text-warm-600 font-semibold mb-0.5">
                      {meta.field_labels?.max_jahre_non_wt || "Max. Jahre Nicht-WT"}
                    </span>
                    <input
                      type="number"
                      min={0}
                      max={50}
                      value={rule.max_jahre_non_wt || 0}
                      onChange={(e) =>
                        updateRule(idx, {
                          max_jahre_non_wt: Number(e.target.value) || 0,
                        })
                      }
                      data-testid={`kv-cap-bypass-maxjahrenonwt-${idx}`}
                      className="w-full rounded-sm border border-ink-300 bg-white px-2 py-1.5 text-sm focus:border-brand-400 focus:outline-none focus-visible:ring-1 focus-visible:ring-brand-400"
                    />
                    {meta.field_helpers?.max_jahre_non_wt && (
                      <span className="mt-1 block text-[10.5px] text-warm-500 leading-relaxed">
                        {meta.field_helpers.max_jahre_non_wt}
                      </span>
                    )}
                  </label>
                )}
                {meta.fields.includes("faktor_non_wt") && (
                  <label className="block text-xs">
                    <span className="block text-warm-600 font-semibold mb-0.5">
                      {meta.field_labels?.faktor_non_wt || "Faktor Nicht-WT"}
                    </span>
                    <input
                      type="number"
                      step="0.05"
                      min={0}
                      max={1}
                      value={rule.faktor_non_wt ?? 0.5}
                      onChange={(e) =>
                        updateRule(idx, {
                          faktor_non_wt: Number(e.target.value) || 0,
                        })
                      }
                      data-testid={`kv-cap-bypass-faktornonwt-${idx}`}
                      className="w-full rounded-sm border border-ink-300 bg-white px-2 py-1.5 text-sm focus:border-brand-400 focus:outline-none focus-visible:ring-1 focus-visible:ring-brand-400"
                    />
                    {meta.field_helpers?.faktor_non_wt && (
                      <span className="mt-1 block text-[10.5px] text-warm-500 leading-relaxed">
                        {meta.field_helpers.faktor_non_wt}
                      </span>
                    )}
                  </label>
                )}
                {/* Iter 20.36.f9 — SV-Status-Max-Dauer-Felder. */}
                {meta.fields.includes("sv_status_kategorie") && (
                  <label className="block text-xs">
                    <span className="block text-warm-600 font-semibold mb-0.5">
                      {meta.field_labels?.sv_status_kategorie || "Kategorie"}
                    </span>
                    <select
                      value={rule.sv_status_kategorie || ""}
                      onChange={(e) =>
                        updateRule(idx, {
                          sv_status_kategorie: e.target.value,
                        })
                      }
                      data-testid={`kv-cap-bypass-svkategorie-${idx}`}
                      className="w-full rounded-sm border border-ink-300 bg-white px-2 py-1.5 text-sm focus:border-brand-400 focus:outline-none focus-visible:ring-1 focus-visible:ring-brand-400"
                    >
                      {(meta.field_options?.sv_status_kategorie || []).map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    {meta.field_helpers?.sv_status_kategorie && (
                      <span className="mt-1 block text-[10.5px] text-warm-500 leading-relaxed">
                        {meta.field_helpers.sv_status_kategorie}
                      </span>
                    )}
                  </label>
                )}
                {meta.fields.includes("max_monate") && (
                  <label className="block text-xs">
                    <span className="block text-warm-600 font-semibold mb-0.5">
                      {meta.field_labels?.max_monate || "Max. Monate"}
                    </span>
                    <input
                      type="number"
                      min={1}
                      max={600}
                      value={rule.max_monate || 0}
                      onChange={(e) =>
                        updateRule(idx, {
                          max_monate: Number(e.target.value) || 0,
                        })
                      }
                      data-testid={`kv-cap-bypass-maxmonate-${idx}`}
                      className="w-full rounded-sm border border-ink-300 bg-white px-2 py-1.5 text-sm focus:border-brand-400 focus:outline-none focus-visible:ring-1 focus-visible:ring-brand-400"
                    />
                    {meta.field_helpers?.max_monate && (
                      <span className="mt-1 block text-[10.5px] text-warm-500 leading-relaxed">
                        {meta.field_helpers.max_monate}
                      </span>
                    )}
                  </label>
                )}
                {/* Iter 20.36.f14.12 — Präsenzdienst-Regel-Modus. */}
                {meta.fields.includes("praesenzdienst_modus") && (
                  <label className="block text-xs sm:col-span-2">
                    <span className="block text-warm-600 font-semibold mb-0.5">
                      {meta.field_labels?.praesenzdienst_modus || "Modus"}
                    </span>
                    <select
                      value={rule.praesenzdienst_modus || "gesetzlich"}
                      onChange={(e) =>
                        updateRule(idx, {
                          praesenzdienst_modus: e.target.value,
                        })
                      }
                      data-testid={`kv-cap-bypass-praesenzdienstmodus-${idx}`}
                      className="w-full rounded-sm border border-ink-300 bg-white px-2 py-1.5 text-sm focus:border-brand-400 focus:outline-none focus-visible:ring-1 focus-visible:ring-brand-400"
                    >
                      {(meta.field_options?.praesenzdienst_modus || []).map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    {meta.field_helpers?.praesenzdienst_modus && (
                      <span className="mt-1 block text-[10.5px] text-warm-500 leading-relaxed">
                        {meta.field_helpers.praesenzdienst_modus}
                      </span>
                    )}
                  </label>
                )}
              </div>

              <label className="flex items-center gap-2 text-xs text-warm-700 pt-1">
                <input
                  type="checkbox"
                  checked={!!rule.aktiv}
                  onChange={(e) => updateRule(idx, { aktiv: e.target.checked })}
                  data-testid={`kv-cap-bypass-active-${idx}`}
                  className="h-3.5 w-3.5 accent-brand-400 cursor-pointer"
                />
                Regel aktiv
              </label>
            </div>
          );
        })}

        <div className="pt-1 space-y-2">
          <div className="text-[10px] uppercase tracking-widest font-semibold text-warm-500">
            Neue Sonderregel hinzufügen
          </div>
          {CAP_BYPASS_GROUPS.map((grp) => {
            const groupTypes = CAP_BYPASS_TYPES.filter((t) => t.group === grp.id);
            if (groupTypes.length === 0) return null;
            return (
              <div key={grp.id} data-testid={`kv-cap-bypass-group-${grp.id}`}>
                <div className="text-[9.5px] uppercase tracking-widest font-semibold text-warm-500/80 mb-1">
                  {grp.title}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {groupTypes.map((t) => {
                    const alreadyHas = rules.some((r) => r.typ === t.typ);
                    return (
                      <button
                        key={t.typ}
                        type="button"
                        onClick={() => addRule(t.typ)}
                        disabled={alreadyHas}
                        data-testid={`kv-cap-bypass-add-${t.typ}`}
                        title={alreadyHas ? "Bereits hinzugefügt" : t.description}
                        className={
                          "inline-flex items-center gap-1 rounded-sm border px-2.5 py-1 text-[11px] font-semibold transition-crisp " +
                          (alreadyHas
                            ? "border-ink-200 text-warm-400 cursor-not-allowed bg-warm-50"
                            : "border-brand-300 text-brand-700 hover:bg-brand-50 hover:border-brand-400")
                        }
                      >
                        + {t.title}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </details>
  );
}


function KvFormModal({ open, mode, initial, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [extractFile, setExtractFile] = useState(null);
  const [extractExcelFile, setExtractExcelFile] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [extractInfo, setExtractInfo] = useState(null);
  const [activeUsers, setActiveUsers] = useState([]);
  const [userPickerQuery, setUserPickerQuery] = useState("");
  const [vordienstReExtract, setVordienstReExtract] = useState({ open: false });
  const fileInputRef = React.useRef(null);
  const excelInputRef = React.useRef(null);

  useEffect(() => {
    if (open) {
      // Normalize gehaltsstufen_tabelle to ensure it's always an array of {gruppe, stufe, jahre}
      const initRows = Array.isArray(initial?.gehaltsstufen_tabelle)
        ? initial.gehaltsstufen_tabelle.map((r) => ({
            gruppe: String(r?.gruppe || ""),
            stufe: String(r?.stufe || ""),
            jahre: String(r?.jahre || ""),
          }))
        : [];
      setForm(initial
        ? { ...EMPTY_FORM, ...initial, gehaltsstufen_tabelle: initRows }
        : EMPTY_FORM);
      setError("");
      setExtractFile(null);
      setExtractExcelFile(null);
      setExtractInfo(null);
      setExtracting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
      if (excelInputRef.current) excelInputRef.current.value = "";
      // Lazy-load active users for the beta-allowlist picker. Failure is
      // non-fatal — the picker simply stays empty in that case.
      (async () => {
        try {
          const { data } = await api.get("/admin/users", { params: { status: "aktiv" } });
          setActiveUsers(
            (data.users || []).filter((u) => (u.role || "user") !== "admin")
          );
        } catch {
          setActiveUsers([]);
        }
      })();
      setUserPickerQuery("");
    }
  }, [open, initial]);

  if (!open) return null;

  const isEdit = mode === "edit";

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const handleFilePick = (e) => {
    const f = e.target.files?.[0] || null;
    setExtractFile(f);
    setError("");
    setExtractInfo(null);
  };

  const handleExcelPick = (e) => {
    const f = e.target.files?.[0] || null;
    setExtractExcelFile(f);
    setError("");
    setExtractInfo(null);
  };

  const handleExtract = async () => {
    if (!extractFile) {
      setError("Bitte zuerst ein PDF-, DOCX- oder TXT-Dokument auswählen.");
      return;
    }
    setError("");
    setExtracting(true);
    try {
      const fd = new FormData();
      fd.append("file", extractFile);
      if (extractExcelFile) {
        fd.append("excel_file", extractExcelFile);
      }
      // Iter 19.68.14 — Admin-Extract läuft synchron auf dem Server
      // (Multi-Pod-Race auf Production hat den Job-Pfad unbrauchbar
      // gemacht). Wir warten hier direkt auf die Response, LLM-Call
      // liegt typisch bei 30-90s. Cloudflare-Ingress-Timeout ~100s.
      const { data } = await api.post("/admin/kvs/extract", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120_000,
      });
      const draft = data?.draft || {};
      const tableRows = Array.isArray(draft.gehaltsstufen_tabelle)
        ? draft.gehaltsstufen_tabelle.map((r) => ({
            gruppe: String(r?.gruppe || ""),
            stufe: String(r?.stufe || ""),
            jahre: String(r?.jahre || ""),
          }))
        : [];

      setForm((f) => ({
        ...f,
        // Only overwrite empty fields with the AI draft, keep any manual edits
        name: f.name?.trim() ? f.name : draft.name || "",
        short_description: f.short_description?.trim()
          ? f.short_description
          : draft.short_description || "",
        ruleset: f.ruleset?.trim() ? f.ruleset : draft.ruleset || "",
        vordienst_anrechnungsregeln: f.vordienst_anrechnungsregeln?.trim()
          ? f.vordienst_anrechnungsregeln
          : draft.vordienst_anrechnungsregeln || "",
        equivalence_check_required: f.equivalence_check_required
          ? f.equivalence_check_required
          : !!draft.equivalence_check_required,
        gehaltsstufen_tabelle:
          (f.gehaltsstufen_tabelle && f.gehaltsstufen_tabelle.length > 0)
            ? f.gehaltsstufen_tabelle
            : tableRows,
      }));
      setExtractInfo({
        filename: draft.source_filename || extractFile.name,
        excel_filename: draft.excel_filename || null,
        model: draft.model,
        chars: draft.source_chars,
        excel_chars: draft.excel_chars || 0,
        truncated: draft.truncated,
        excel_truncated: draft.excel_truncated,
        tables_count: tableRows.length,
      });
      toast.success(
        tableRows.length > 0
          ? `Entwurf erstellt: ${tableRows.length} Gehaltsstufen-Zeilen erkannt. Bitte prüfen.`
          : "Entwurf erstellt. Bitte prüfen und ggf. anpassen."
      );
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 402) {
        setError(
          typeof detail === "string"
            ? detail
            : "Das Guthaben des Emergent Universal Keys ist aufgebraucht. Bitte unter Profil → Universal Key → Add Balance aufladen."
        );
      } else if (status === 429) {
        setError(
          "Die KI ist gerade überlastet (Rate-Limit). Bitte einen Moment warten und erneut versuchen."
        );
      } else {
        setError(extractError(err, "Extraktion fehlgeschlagen."));
      }
    } finally {
      setExtracting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.short_description.trim() || !form.ruleset.trim()) {
      setError("Bitte füllen Sie Name, Kurzbeschreibung und Regelwerk aus.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        kuerzel: (form.kuerzel || "").trim(),
        short_description: form.short_description.trim(),
        ruleset: form.ruleset.trim(),
        vordienst_anrechnungsregeln: (form.vordienst_anrechnungsregeln || "").trim(),
        gehaltsstufen_tabelle: (form.gehaltsstufen_tabelle || [])
          .map((r) => ({
            gruppe: (r.gruppe || "").trim(),
            stufe: (r.stufe || "").trim(),
            jahre: (r.jahre || "").trim(),
          }))
          .filter((r) => r.gruppe || r.stufe || r.jahre),
        equivalence_check_required: !!form.equivalence_check_required,
        is_visible: !!form.is_visible,
        beta_user_ids: Array.isArray(form.beta_user_ids) ? form.beta_user_ids : [],
        // Iter 19.66.2 — Cap-Bypass-Sonderregeln immer im Payload mitsenden
        // (Backend handhabt leere Liste als "Sonderregeln entfernt").
        cap_bypass_sonderregeln: Array.isArray(form.cap_bypass_sonderregeln)
          ? form.cap_bypass_sonderregeln
          : [],
        // Iter 20.21 — Admin-Zusatzhinweis (markdown-fähig, keine Logik).
        zusatz_hinweis: (form.zusatz_hinweis || "").trim(),
        // Iter 20.33 — Native KV-Reihenfolge der Beschäftigungsgruppen.
        gruppen_order: (form.gruppen_order || "").trim(),
        // Iter 20.36 — KV-spezifisches Wording.
        equivalence_terminology: (form.equivalence_terminology || "gleichwertigkeit").trim() || "gleichwertigkeit",
      };
      if (isEdit) {
        // Iter 19.44.2 — Option C: Preview-Sync ausführen, BEFORE PATCH.
        // Falls Orphans entfernt werden würden, Confirmation-Dialog zeigen.
        let confirmOrphanDeletion = false;
        const markdownChanged =
          (payload.vordienst_anrechnungsregeln || "") !==
          (initial.vordienst_anrechnungsregeln || "").trim();
        if (markdownChanged) {
          try {
            const { data: preview } = await api.post(
              `/admin/kvs/${initial.id}/preview-sync`,
              { vordienst_anrechnungsregeln: payload.vordienst_anrechnungsregeln || "" },
            );
            const orphans = Array.isArray(preview?.orphans) ? preview.orphans : [];
            if (orphans.length > 0) {
              const labelList = orphans
                .map((o) => `• ${o.label} (${o.months} Monate)`)
                .join("\n");
              const ok = window.confirm(
                `Folgende ${orphans.length} Ausbildungsregel${orphans.length === 1 ? "" : "n"} ` +
                  "werden aus diesem KV entfernt, da sie nicht (mehr) im Markdown stehen:\n\n" +
                  labelList +
                  "\n\nDie Regeln verschwinden danach aus dem Schritt-3-Dropdown und " +
                  "werden auch nicht mehr aus Lebensläufen erkannt.\n\nFortfahren?",
              );
              if (!ok) {
                setSaving(false);
                return;
              }
              confirmOrphanDeletion = true;
            }
          } catch (_previewErr) {
            // Preview-Fail ist nicht blockierend — Save geht weiter mit
            // konservativem Default (keine Orphan-Löschung).
          }
        }
        const url =
          `/admin/kvs/${initial.id}` +
          (confirmOrphanDeletion ? "?confirm_orphan_deletion=true" : "");
        const { data } = await api.patch(url, payload);
        toast.success(`„${data.name}" gespeichert.`);
        const warns = Array.isArray(data?.sync_warnings) ? data.sync_warnings : [];
        for (const w of warns) {
          toast.message(w, { duration: 8000 });
        }
      } else {
        const { data } = await api.post("/admin/kvs", payload);
        toast.success(`„${data.name}" angelegt.`);
      }
      onSaved();
    } catch (err) {
      setError(extractError(err, "Speichern fehlgeschlagen."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-ink-900/40 backdrop-blur-sm"
      data-testid="kv-form-modal"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="w-full max-w-3xl bg-white border border-ink-200 rounded-sm relative max-h-[92vh] flex flex-col">
        <div className="absolute top-0 left-0 h-1 w-24 bg-brand-400" aria-hidden="true" />

        <div className="flex items-start justify-between px-8 pt-8 pb-4 border-b border-ink-200">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-semibold text-warm-500 mb-1">
              {isEdit ? "Bearbeiten" : "Neu anlegen"}
            </div>
            <h2 className="font-cabinet text-2xl tracking-tight font-medium text-ink-900">
              Kollektivvertrag
            </h2>
          </div>
          <button
            onClick={onClose}
            data-testid="kv-modal-close"
            className="text-warm-500 hover:text-ink-900 transition-crisp"
            aria-label="Schließen"
          >
            <X size={20} weight="bold" />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex-1 overflow-y-auto px-8 py-6 space-y-5"
          data-testid="kv-form"
        >
          {!isEdit && (
            <section
              className="border border-brand-300 bg-brand-50 rounded-sm p-5 relative"
              data-testid="kv-extract-section"
            >
              <div className="flex items-start gap-3 mb-3">
                <Sparkle size={20} weight="duotone" className="text-brand-500 mt-0.5 shrink-0" />
                <div className="flex-1">
                  <div className="font-medium text-ink-900 text-sm">
                    Aus KV-Dokument automatisch befüllen
                  </div>
                  <div className="text-xs text-warm-700 mt-1 leading-relaxed">
                    Laden Sie ein KV-Dokument (PDF, DOCX oder TXT) hoch — die KI
                    erstellt einen <strong>Entwurf</strong> für die Felder unten.
                    Sie können den Entwurf anschließend prüfen und anpassen, bevor
                    Sie endgültig speichern. Die Datei wird ausschließlich
                    In-Memory verarbeitet und nicht gespeichert.
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                <label
                  htmlFor="kv-extract-file"
                  className="flex-1 inline-flex items-center gap-2 px-3 py-2 border border-ink-300 bg-white rounded-sm text-sm text-ink-900 cursor-pointer hover:border-brand-400 transition-crisp min-w-0"
                  data-testid="kv-extract-file-label"
                >
                  <UploadSimple size={14} weight="bold" className="text-warm-600 shrink-0" />
                  <span className="truncate">
                    {extractFile ? extractFile.name : "Datei wählen (PDF, DOCX, TXT)"}
                  </span>
                </label>
                <input
                  id="kv-extract-file"
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.txt,.docx,application/pdf,text/plain,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  onChange={handleFilePick}
                  className="sr-only"
                  data-testid="kv-extract-file-input"
                />
                <Button
                  type="button"
                  variant="brand"
                  onClick={handleExtract}
                  disabled={extracting || !extractFile}
                  data-testid="kv-extract-btn"
                >
                  <Sparkle size={14} weight="bold" />
                  {extracting ? "KI analysiert…" : "Mit KI extrahieren"}
                </Button>
              </div>

              <div className="mt-2 flex flex-col sm:flex-row sm:items-center gap-2">
                <label
                  htmlFor="kv-extract-excel"
                  className="flex-1 inline-flex items-center gap-2 px-3 py-2 border border-dashed border-ink-300 bg-white/60 rounded-sm text-xs text-warm-700 cursor-pointer hover:border-brand-400 transition-crisp min-w-0"
                  data-testid="kv-extract-excel-label"
                >
                  <UploadSimple size={13} weight="bold" className="text-warm-500 shrink-0" />
                  <span className="truncate">
                    {extractExcelFile
                      ? extractExcelFile.name
                      : "Optional: Excel-Anrechnungs-Tabelle anhängen (.xlsx)"}
                  </span>
                </label>
                <input
                  id="kv-extract-excel"
                  ref={excelInputRef}
                  type="file"
                  accept=".xlsx,.xls,.xlsm,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                  onChange={handleExcelPick}
                  className="sr-only"
                  data-testid="kv-extract-excel-input"
                />
                {extractExcelFile && (
                  <button
                    type="button"
                    onClick={() => {
                      setExtractExcelFile(null);
                      if (excelInputRef.current) excelInputRef.current.value = "";
                    }}
                    aria-label="Excel-Anhang entfernen"
                    data-testid="kv-extract-excel-remove"
                    className="text-warm-400 hover:text-status-abgelehnt-text transition-crisp shrink-0"
                  >
                    <X size={13} weight="bold" />
                  </button>
                )}
              </div>
              <div className="mt-1.5 text-[10px] text-warm-500 leading-relaxed">
                Tipp: Bei vorhandener Anrechnungs-Tabelle (z.B. Excel mit Höchstdauern, Branchen-Mapping)
                füllt die KI das Feld <em>&bdquo;Spezifische Anrechnungsregeln&ldquo;</em> deutlich präziser aus.
              </div>

              {extracting && (
                <div className="mt-3" data-testid="kv-extract-loading">
                  <div className="loading-bar h-0.5" />
                  <div className="mt-2 text-[11px] text-warm-700">
                    Das Dokument wird analysiert. Dies kann je nach Umfang
                    10–60 Sekunden dauern.
                  </div>
                </div>
              )}

              {extractInfo && !extracting && (
                <div
                  className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-warm-700"
                  data-testid="kv-extract-info"
                >
                  <span className="inline-flex items-center gap-1">
                    <FileText size={11} weight="bold" />
                    {extractInfo.filename}
                  </span>
                  {extractInfo.excel_filename && (
                    <span className="inline-flex items-center gap-1" data-testid="kv-extract-info-excel">
                      <FileText size={11} weight="bold" className="text-status-aktiv-text" />
                      {extractInfo.excel_filename}
                      {extractInfo.excel_truncated ? " · gekürzt" : ""}
                    </span>
                  )}
                  <span>
                    {extractInfo.chars?.toLocaleString("de-AT")} Zeichen ·{" "}
                    {extractInfo.model}
                    {extractInfo.truncated ? " · gekürzt" : ""}
                  </span>
                  <span className="text-brand-700 font-medium">
                    Entwurf erstellt — bitte unten prüfen und anpassen.
                  </span>
                </div>
              )}
            </section>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-[1fr_180px] gap-4">
            <div>
              <Label htmlFor="kv-name">KV-Name</Label>
              <Input
                id="kv-name"
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="z.B. KV Handel Angestellte"
                required
                maxLength={160}
                data-testid="kv-input-name"
              />
            </div>
            <div>
              <Label htmlFor="kv-kuerzel">
                Kürzel
                <span className="ml-2 text-[10px] tracking-normal text-warm-400 normal-case">
                  (Badge auf KV-Karte)
                </span>
              </Label>
              <Input
                id="kv-kuerzel"
                name="kuerzel"
                value={form.kuerzel}
                onChange={handleChange}
                placeholder="z.B. KV HANDEL"
                maxLength={60}
                data-testid="kv-input-kuerzel"
                style={{ textTransform: "uppercase" }}
              />
              <div className="mt-1 text-[10px] text-warm-400">
                Optional — wenn leer, wird aus dem KV-Namen automatisch ein Monogramm erzeugt.
              </div>
            </div>
          </div>

          <div>
            <Label htmlFor="kv-short">
              Kurzbeschreibung der Anrechnungsregeln
              <span className="ml-2 text-[10px] tracking-normal text-warm-400 normal-case">
                (für Frontend-Anzeige)
              </span>
            </Label>
            <textarea
              id="kv-short"
              name="short_description"
              value={form.short_description}
              onChange={handleChange}
              rows={3}
              maxLength={600}
              placeholder="Knappe, allgemein verständliche Erklärung der Anrechnungslogik."
              required
              data-testid="kv-input-short"
              className="w-full rounded-sm border border-ink-300 bg-white px-3 py-2.5 text-sm text-ink-900 placeholder:text-warm-400 transition-crisp focus:border-brand-400 focus:outline-none focus-visible:ring-1 focus-visible:ring-brand-400 resize-y"
            />
            <div className="mt-1 flex justify-between text-[10px] text-warm-400">
              <span>Wird Endkundinnen nach der Einstufung gezeigt.</span>
              <span>{form.short_description.length} / 600</span>
            </div>
          </div>

          <div>
            <Label htmlFor="kv-ruleset">
              Komprimiertes Regelwerk
              <span className="ml-2 text-[10px] tracking-normal text-warm-400 normal-case">
                (KI-Kontext)
              </span>
            </Label>
            <textarea
              id="kv-ruleset"
              name="ruleset"
              value={form.ruleset}
              onChange={handleChange}
              rows={10}
              maxLength={20000}
              placeholder="Stark verkürztes, präzises Regelwerk in Stichpunkten — wird als Kontext an die KI übergeben."
              required
              data-testid="kv-input-ruleset"
              className="w-full rounded-sm border border-ink-300 bg-white px-3 py-2.5 text-sm text-ink-900 placeholder:text-warm-400 transition-crisp focus:border-brand-400 focus:outline-none focus-visible:ring-1 focus-visible:ring-brand-400 resize-y font-mono"
            />
            <div className="mt-1 flex justify-between text-[10px] text-warm-400">
              <span>Tipp: nur entscheidungs-relevante Regeln, keine Originaltext-Auszüge.</span>
              <span>{form.ruleset.length} / 20.000</span>
            </div>
          </div>

          <div>
            <div className="flex items-start justify-between gap-3 flex-wrap mb-1.5">
              <Label htmlFor="kv-vordienst-regeln" className="!mb-0">
                Spezifische Anrechnungsregeln für Versicherungsdatenauszug
                <span className="ml-2 text-[10px] tracking-normal text-warm-400 normal-case">
                  (optional, primäre KI-Quelle bei Vordienstzeit-Bewertung)
                </span>
              </Label>
              {/* Only show the "Neu auslesen"-Button when editing an existing KV. */}
              {mode === "edit" && initial?.id && (
                <button
                  type="button"
                  onClick={() => setVordienstReExtract({ open: true })}
                  data-testid="kv-vordienst-reextract-open"
                  className="inline-flex items-center gap-1.5 rounded-sm border border-brand-300 bg-brand-50 px-3 py-1.5 text-[11px] font-semibold text-brand-700 hover:bg-brand-100 transition-crisp shrink-0"
                  title="Korrigiertes Excel hochladen und Regeln automatisch neu auslesen"
                >
                  <Sparkle size={11} weight="bold" />
                  Excel neu auslesen
                </button>
              )}
            </div>
            <textarea
              id="kv-vordienst-regeln"
              name="vordienst_anrechnungsregeln"
              value={form.vordienst_anrechnungsregeln || ""}
              onChange={handleChange}
              rows={8}
              maxLength={10000}
              placeholder={
                "Höchstanrechnungsdauer: ___ Jahre\n" +
                "Anrechenbar: gleiche/vergleichbare Branche zu 100 %, branchenfremd zu __ %\n" +
                "Bonus: HAK-Matura + 2 Jahre, Lehre einschlägig + 3 Jahre, …\n" +
                "Karenz / Präsenzdienst / Zivildienst: __ \n" +
                "Geringfügige Beschäftigung: nicht anrechenbar"
              }
              data-testid="kv-input-vordienst-regeln"
              className="w-full rounded-sm border border-ink-300 bg-white px-3 py-2.5 text-sm text-ink-900 placeholder:text-warm-400 transition-crisp focus:border-brand-400 focus:outline-none focus-visible:ring-1 focus-visible:ring-brand-400 resize-y font-mono"
            />
            <div className="mt-1 flex justify-between text-[10px] text-warm-400">
              <span>
                Wird in Schritt 3 + 4 als <strong>primäre Quelle</strong> für Anrechenbarkeits-Entscheidungen verwendet (überschreibt das allgemeine Regelwerk).
              </span>
              <span>{(form.vordienst_anrechnungsregeln || "").length} / 10.000</span>
            </div>

            {/* Iter 19.63.96 — Parser-Hinweis: erklärt, welche Section-Header
                automatisch erkannt werden. Hintergrund (Bug-Report KV Handel
                Arbeiter): wenn User-Header wie `**Anrechenbar (lit. a):**`
                statt `**Voll anrechenbar:**` verwendet werden, blieb der
                voll-Bucket leer und das LLM hatte freie Hand für Faktor-
                Halluzinationen. Iter 95 macht den Parser toleranter, dieser
                Hinweis macht das Erwartete für den Admin sichtbar. */}
            <details
              className="mt-2 rounded-sm border border-ink-200 bg-warm-50/40 text-xs"
              data-testid="kv-vordienst-parser-hint"
            >
              <summary className="cursor-pointer px-3 py-2 font-medium text-ink-800 hover:bg-warm-50 transition-crisp select-none">
                <Info
                  size={12}
                  weight="bold"
                  className="inline-block mr-1.5 -mt-0.5 text-brand-500"
                />
                So liest der Parser Faktoren aus deinem Text
              </summary>
              <div className="px-3 pb-3 pt-1 space-y-2 leading-relaxed text-warm-700">
                <p>
                  Der Parser erkennt diese Section-Header automatisch und ordnet
                  jede dort gelistete Tätigkeit dem passenden Bucket zu:
                </p>
                <ul className="space-y-1 ml-4 list-disc">
                  <li>
                    <code className="font-mono text-[11px] text-emerald-700 bg-white border border-ink-200 rounded-sm px-1 py-0.5">
                      **Voll anrechenbar:**
                    </code>{" "}
                    /{" "}
                    <code className="font-mono text-[11px] text-emerald-700 bg-white border border-ink-200 rounded-sm px-1 py-0.5">
                      **Anrechenbar:**
                    </code>{" "}
                    /{" "}
                    <code className="font-mono text-[11px] text-emerald-700 bg-white border border-ink-200 rounded-sm px-1 py-0.5">
                      **Anrechenbar (lit. a):**
                    </code>{" "}
                    /{" "}
                    <code className="font-mono text-[11px] text-emerald-700 bg-white border border-ink-200 rounded-sm px-1 py-0.5">
                      **100 % anrechenbar:**
                    </code>{" "}
                    → Faktor 1,0
                  </li>
                  <li>
                    <code className="font-mono text-[11px] text-amber-700 bg-white border border-ink-200 rounded-sm px-1 py-0.5">
                      **Hälftig anrechenbar:**
                    </code>{" "}
                    /{" "}
                    <code className="font-mono text-[11px] text-amber-700 bg-white border border-ink-200 rounded-sm px-1 py-0.5">
                      **50 % anrechenbar:**
                    </code>{" "}
                    → Faktor 0,5
                  </li>
                  <li>
                    <code className="font-mono text-[11px] text-red-700 bg-white border border-ink-200 rounded-sm px-1 py-0.5">
                      **Nicht anrechenbar:**
                    </code>{" "}
                    → Faktor 0,0
                  </li>
                </ul>
                <p className="pt-1 border-t border-ink-200">
                  <strong className="text-ink-900">Wichtig:</strong> Wenn dein
                  KV keinen halbierten Faktor vorsieht, lass die{" "}
                  <code className="font-mono text-[11px] text-warm-700">
                    Hälftig
                  </code>
                  -Sektion einfach weg. Das System setzt dann Faktor 1,0 für
                  alle anrechenbaren Tätigkeiten und verbietet dem LLM eigen-
                  mächtige Hälftig-Interpretationen.
                </p>
              </div>
            </details>
          </div>

          {/* Iter 19.66.2 — Cap-Bypass-Sonderregeln. KV-spezifische
              "Selber-Arbeitgeber"-Bedingungen, die je KV unterschiedlich
              sind (KV Handel Arbeiter: 24-Monate-Lückenregel; KV Gewerbe:
              Vorarbeiterzeit 50 %/5 J + Gleiche/Höhere Gruppe). */}
          <CapBypassSonderregelnSection
            value={form.cap_bypass_sonderregeln || []}
            onChange={(next) => setForm((f) => ({ ...f, cap_bypass_sonderregeln: next }))}
          />

          {/* Iter 20.21 — Admin-Zusatzhinweis (markdown-fähig, ohne Logik).
              Wird im Schritt-3-KV-Sonderregeln-Card als eigener Info-Block
              angezeigt. Ideal für kurze Klarstellungen, die kein Regel-
              Objekt sind (z.B. „Der KV verlangt bei Präsenzdienst zusätzlich
              den §-14-Vermerk im Dienstzettel"). Markdown-Support:
              **fett**, *kursiv*, - Listen, [Links](https://…). */}
          <div className="p-4 border border-ink-200 rounded-sm bg-warm-50/40">
            <Label
              htmlFor="kv-zusatz-hinweis"
              className="text-sm font-medium text-ink-700 mb-1 block"
            >
              Zusatzhinweis (optional, nur Anzeige — keine Logik)
            </Label>
            <p className="text-[11.5px] text-ink-500 mb-2 leading-snug">
              Freitext-Hinweis, den Sachbearbeiter in Schritt 3 unter den
              KV-Sonderregeln als Info-Block sehen. Markdown erlaubt
              (<code>**fett**</code>, <code>- Liste</code>,{" "}
              <code>[Link](https://…)</code>). Max. 4000 Zeichen.
            </p>
            <textarea
              id="kv-zusatz-hinweis"
              name="zusatz_hinweis"
              value={form.zusatz_hinweis || ""}
              onChange={(e) =>
                setForm((f) => ({ ...f, zusatz_hinweis: e.target.value }))
              }
              rows={4}
              maxLength={4000}
              placeholder="Beispiel: **Wichtig:** Bei Präsenzdienst ist zusätzlich der §-14-Vermerk im Dienstzettel erforderlich."
              className="w-full border border-ink-200 rounded-sm bg-white px-3 py-2 text-sm font-mono text-ink-800 placeholder:text-ink-400 focus:outline-none focus:ring-2 focus:ring-ink-300"
              data-testid="kv-input-zusatz-hinweis"
            />
            <div className="mt-1 flex justify-end text-[11px] text-ink-500">
              <span data-testid="kv-input-zusatz-hinweis-counter">
                {(form.zusatz_hinweis || "").length} / 4000
              </span>
            </div>
          </div>

          {/* Iter 20.33 — Native KV-Reihenfolge der Beschäftigungsgruppen.
              Kommaseparierte Liste in aufsteigender Hierarchie. Frontend
              `GroupOptions` rendert die Gruppen-Karten strikt in dieser
              Reihenfolge; leer → heuristischer Fallback (Römisch → Arabisch
              → Alphabet). */}
          <div className="p-4 border border-ink-200 rounded-sm bg-warm-50/40">
            <Label
              htmlFor="kv-gruppen-order"
              className="text-sm font-medium text-ink-700 mb-1 block"
            >
              Reihenfolge der Beschäftigungsgruppen (aufsteigend, kommasepariert)
            </Label>
            <p className="text-[11.5px] text-ink-500 mb-2 leading-snug">
              Native Hierarchie der Beschäftigungsgruppen des KV in
              aufsteigender Reihenfolge. Beispiel Handel:{" "}
              <code>A, B, C, D, E, F, G, H</code>. Beispiel IT:{" "}
              <code>ZT, AT, ST1, ST2, LT</code>. Leer lassen → Sortierung
              erfolgt heuristisch (Römisch → Arabisch → Alphabet).
            </p>
            <input
              type="text"
              id="kv-gruppen-order"
              name="gruppen_order"
              value={form.gruppen_order || ""}
              onChange={(e) =>
                setForm((f) => ({ ...f, gruppen_order: e.target.value }))
              }
              maxLength={2000}
              placeholder="z.B. A, B, C, D, E, F, G, H"
              className="w-full border border-ink-200 rounded-sm bg-white px-3 py-2 text-sm font-mono text-ink-800 placeholder:text-ink-400 focus:outline-none focus:ring-2 focus:ring-ink-300"
              data-testid="kv-input-gruppen-order"
            />
            <div className="mt-1 flex justify-end text-[11px] text-ink-500">
              <span data-testid="kv-input-gruppen-order-counter">
                {(form.gruppen_order || "").length} / 2000
              </span>
            </div>
          </div>

          <GehaltsstufenEditor
            rows={form.gehaltsstufen_tabelle || []}
            onChange={(rows) => setForm((f) => ({ ...f, gehaltsstufen_tabelle: rows }))}
          />

          <label
            className="flex items-start gap-3 p-4 border border-ink-200 rounded-sm bg-warm-50 cursor-pointer hover:border-ink-300 transition-crisp"
            data-testid="kv-input-equivalence-label"
          >
            <input
              type="checkbox"
              name="equivalence_check_required"
              checked={!!form.equivalence_check_required}
              onChange={handleChange}
              className="mt-0.5 h-4 w-4 accent-brand-400 cursor-pointer"
              data-testid="kv-input-equivalence"
            />
            <div className="flex-1">
              <div className="font-medium text-ink-900 text-sm">
                Prüfung auf {getEquivalenceTerms(form.equivalence_terminology).noun} der Tätigkeit erforderlich
              </div>
              <div className="text-xs text-warm-600 mt-1 leading-relaxed">
                Wenn aktiviert, fragt das Werkzeug zusätzlich nach einer
                Beurteilung der {getEquivalenceTerms(form.equivalence_terminology).noun} fachfremder Vortätigkeiten.
              </div>
            </div>
          </label>

          {/* Iter 20.36 — KV-spezifisches Wording „Gleichwertigkeit" vs
              „Facheinschlägigkeit". Sobald der Wert umgeschaltet wird, ändert
              sich UI-weit das Substantiv, das Adjektiv und die zusammen-
              gesetzten Formen — inkl. LLM-Prompt-Direktiven. */}
          <div className="p-4 border border-ink-200 rounded-sm bg-warm-50/40" data-testid="kv-equivalence-terminology-block">
            <div className="text-sm font-medium text-ink-900 mb-1">
              Fachbezeichnung dieser Prüfung
            </div>
            <div className="text-[11.5px] text-ink-500 mb-3 leading-snug">
              Wählt das Wording, das im gesamten Frontend und in den LLM-
              Ausgaben (Begründungen, Zusammenfassung) für diesen KV verwendet
              wird. Die Semantik bleibt identisch — der Sachbearbeiter prüft
              es manuell.
            </div>
            <div className="flex flex-col gap-2">
              {[
                { key: "gleichwertigkeit", label: "Gleichwertigkeit (Standard)" },
                { key: "facheinschlaegigkeit", label: "Facheinschlägigkeit (WT-KV)" },
              ].map((opt) => (
                <label
                  key={opt.key}
                  className="flex items-center gap-2 cursor-pointer text-sm text-ink-800"
                  data-testid={`kv-equivalence-terminology-${opt.key}`}
                >
                  <input
                    type="radio"
                    name="equivalence_terminology"
                    value={opt.key}
                    checked={(form.equivalence_terminology || "gleichwertigkeit") === opt.key}
                    onChange={(e) => setForm((f) => ({ ...f, equivalence_terminology: e.target.value }))}
                    className="h-4 w-4 accent-brand-400 cursor-pointer"
                  />
                  <span>{opt.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Visibility + Beta-Allowlist */}
          <div
            className="border border-ink-200 rounded-sm bg-white"
            data-testid="kv-visibility-block"
          >
            <label
              className="flex items-start gap-3 p-4 cursor-pointer hover:bg-warm-50 transition-crisp"
              data-testid="kv-input-visible-label"
            >
              <input
                type="checkbox"
                name="is_visible"
                checked={!!form.is_visible}
                onChange={handleChange}
                className="mt-0.5 h-4 w-4 accent-brand-400 cursor-pointer"
                data-testid="kv-input-visible"
              />
              <div className="flex-1">
                <div className="font-medium text-ink-900 text-sm inline-flex items-center gap-2">
                  {form.is_visible ? (
                    <Eye size={14} weight="bold" className="text-status-aktiv-text" />
                  ) : (
                    <EyeSlash size={14} weight="bold" className="text-warm-500" />
                  )}
                  Für alle Anwender sichtbar
                </div>
                <div className="text-xs text-warm-600 mt-1 leading-relaxed">
                  {form.is_visible
                    ? "Dieser KV wird in der Anwender-Auswahl angezeigt. Deaktivieren, um den KV vorübergehend auszublenden."
                    : "KV ist versteckt — nur Administratoren und unten gelistete Beta-Anwender sehen ihn."}
                </div>
              </div>
            </label>

            {/* Beta allowlist — always editable. Most useful while is_visible=false,
                but admins may pre-stage it before unhiding. */}
            <div className="border-t border-ink-200 px-4 py-4">
              <div className="flex items-center gap-2 mb-2">
                <UsersThree size={14} weight="bold" className="text-warm-500" />
                <span className="text-[11px] uppercase tracking-widest font-semibold text-warm-500">
                  Beta-Zugang für ausgewählte Anwender
                </span>
                {(form.beta_user_ids || []).length > 0 && (
                  <span
                    className="text-[10px] uppercase tracking-widest font-semibold px-1.5 py-0.5 rounded-sm bg-brand-50 text-brand-700 border border-brand-100"
                    data-testid="kv-beta-count"
                  >
                    {form.beta_user_ids.length} freigeschaltet
                  </span>
                )}
              </div>
              <p className="text-xs text-warm-600 leading-relaxed mb-3">
                Diese Anwender sehen den KV auch wenn er ausgeblendet ist —
                nützlich für Fachpersonal-Tests bevor ein neuer KV
                breitflächig freigegeben wird.
              </p>
              {/* Selected pills */}
              {(form.beta_user_ids || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-3" data-testid="kv-beta-selected">
                  {form.beta_user_ids.map((uid) => {
                    const u = activeUsers.find((x) => x.id === uid);
                    const label = u
                      ? `${u.name || u.email}${u.company ? ` · ${u.company}` : ""}`
                      : uid;
                    return (
                      <span
                        key={uid}
                        className="inline-flex items-center gap-1.5 px-2 py-1 rounded-sm bg-ink-900 text-white text-[11px]"
                      >
                        {label}
                        <button
                          type="button"
                          onClick={() =>
                            setForm((f) => ({
                              ...f,
                              beta_user_ids: (f.beta_user_ids || []).filter(
                                (x) => x !== uid
                              ),
                            }))
                          }
                          className="hover:text-brand-300 transition-crisp"
                          aria-label={`„${label}" entfernen`}
                          data-testid={`kv-beta-remove-${uid}`}
                        >
                          <X size={11} weight="bold" />
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
              <Input
                type="text"
                placeholder="Anwender suchen (Name, E-Mail oder Firma)…"
                value={userPickerQuery}
                onChange={(e) => setUserPickerQuery(e.target.value)}
                data-testid="kv-beta-search"
              />
              {userPickerQuery.trim().length > 0 && (
                <div className="mt-2 max-h-44 overflow-y-auto border border-ink-200 rounded-sm divide-y divide-ink-200">
                  {activeUsers
                    .filter((u) => {
                      const q = userPickerQuery.trim().toLowerCase();
                      return (
                        !(form.beta_user_ids || []).includes(u.id) &&
                        ((u.name || "").toLowerCase().includes(q) ||
                          (u.email || "").toLowerCase().includes(q) ||
                          (u.company || "").toLowerCase().includes(q))
                      );
                    })
                    .slice(0, 12)
                    .map((u) => (
                      <button
                        key={u.id}
                        type="button"
                        onClick={() => {
                          setForm((f) => ({
                            ...f,
                            beta_user_ids: [
                              ...(f.beta_user_ids || []),
                              u.id,
                            ],
                          }));
                          setUserPickerQuery("");
                        }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-warm-50 transition-crisp flex items-center justify-between gap-3"
                        data-testid={`kv-beta-add-${u.id}`}
                      >
                        <span className="min-w-0">
                          <span className="block font-medium text-ink-900 truncate">
                            {u.name || u.email}
                          </span>
                          <span className="block text-xs text-warm-600 truncate">
                            {u.email}
                            {u.company ? ` · ${u.company}` : ""}
                          </span>
                        </span>
                        <Plus size={14} weight="bold" className="text-warm-500 shrink-0" />
                      </button>
                    ))}
                  {activeUsers.filter(
                    (u) =>
                      !(form.beta_user_ids || []).includes(u.id) &&
                      ((u.name || "").toLowerCase().includes(userPickerQuery.trim().toLowerCase()) ||
                        (u.email || "").toLowerCase().includes(userPickerQuery.trim().toLowerCase()) ||
                        (u.company || "").toLowerCase().includes(userPickerQuery.trim().toLowerCase()))
                  ).length === 0 && (
                    <div className="px-3 py-2 text-xs text-warm-500">
                      Keine passenden Anwender gefunden.
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {error && (
            <div
              role="alert"
              data-testid="kv-form-error"
              className="flex items-start gap-2 border border-status-abgelehnt-border bg-status-abgelehnt-bg px-3 py-2 rounded-sm text-sm text-status-abgelehnt-text"
            >
              <Warning size={16} weight="fill" className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="flex items-start gap-2 text-[11px] text-warm-500 pt-2 border-t border-ink-100">
            <Info size={12} weight="bold" className="mt-0.5 shrink-0" />
            In dieser Datenbank werden niemals Endkunden-Daten oder Datei-Uploads
            gespeichert — ausschließlich KV-Stammdaten.
          </div>
        </form>

        <div className="px-8 py-4 border-t border-ink-200 flex items-center justify-end gap-3">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={saving}
            data-testid="kv-cancel-btn"
          >
            Abbrechen
          </Button>
          <Button
            type="button"
            variant="brand"
            onClick={handleSubmit}
            disabled={saving}
            data-testid="kv-save-btn"
          >
            <CheckCircle size={14} weight="bold" />
            {saving
              ? "Wird gespeichert…"
              : isEdit
              ? "Änderungen speichern"
              : "Kollektivvertrag anlegen"}
          </Button>
        </div>
      </div>

      {/* Vordienst-Re-Extract Dialog — overlay on top of the form modal */}
      {vordienstReExtract.open && (
        <VordienstReExtractDialog
          kvId={initial?.id}
          currentText={form.vordienst_anrechnungsregeln || ""}
          onClose={() => setVordienstReExtract({ open: false })}
          onApply={(newText) => {
            setForm((f) => ({ ...f, vordienst_anrechnungsregeln: newText }));
            setVordienstReExtract({ open: false });
          }}
        />
      )}
    </div>
  );
}

export default function AdminKvsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isSuperadmin = (user?.role || "") === "superadmin";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create"); // create | edit
  const [editing, setEditing] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/kvs");
      setItems(data.items || []);
    } catch (err) {
      toast.error(extractError(err, "Kollektivverträge konnten nicht geladen werden."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (k) =>
        k.name.toLowerCase().includes(q) ||
        (k.short_description || "").toLowerCase().includes(q)
    );
  }, [items, search]);

  const openCreate = () => {
    setModalMode("create");
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = (kv) => {
    setModalMode("edit");
    setEditing(kv);
    setModalOpen(true);
  };

  const onSaved = async () => {
    setModalOpen(false);
    await load();
  };

  const remove = async (kv) => {
    if (
      !window.confirm(
        `„${kv.name}" wirklich endgültig löschen? Diese Aktion kann nicht rückgängig gemacht werden.`
      )
    )
      return;
    setBusyId(kv.id);
    try {
      await api.delete(`/admin/kvs/${kv.id}`);
      toast.success("Kollektivvertrag entfernt.");
      await load();
    } catch (err) {
      toast.error(extractError(err, "Löschen fehlgeschlagen."));
    } finally {
      setBusyId(null);
    }
  };

  const duplicate = async (kv) => {
    // Iter 19.38: Duplicate the KV including all rulesets. The copy is
    // hidden (is_visible=false) by default — the superadmin reviews and
    // publishes it manually.
    setBusyId(kv.id);
    try {
      const { data } = await api.post(`/admin/kvs/${kv.id}/duplicate`);
      toast.success(`Kopie angelegt: „${data?.name || "Kollektivvertrag"}" (ausgeblendet).`);
      await load();
    } catch (err) {
      toast.error(extractError(err, "Duplizieren fehlgeschlagen."));
    } finally {
      setBusyId(null);
    }
  };

  const stats = useMemo(() => {
    const total = items.length;
    const equivalence = items.filter((k) => k.equivalence_check_required).length;
    return { total, equivalence };
  }, [items]);

  return (
    <AdminLayout
      title={
        <>
          Kollektiv&shy;verträge<span className="text-brand-400">.</span>
        </>
      }
      subtitle="Verwalten Sie die Stammdaten der unterstützten Kollektivverträge. Diese Daten dienen als Wissensbasis für das KV-Einstufungs-Werkzeug."
    >
      <Toaster position="top-right" richColors closeButton />
      <div data-testid="admin-kvs-page">
        {!isSuperadmin && (
          <div
            data-testid="kv-readonly-banner"
            className="mb-6 flex items-start gap-3 border border-ink-200 bg-warm-50 px-4 py-3 rounded-sm"
          >
            <Lock size={16} weight="bold" className="text-warm-600 mt-0.5 shrink-0" />
            <div className="text-sm text-warm-800 leading-relaxed">
              <strong className="text-ink-900">Nur Lese-Zugriff:</strong>{" "}
              Sie können die hinterlegten Kollektivverträge einsehen.
              Bearbeiten, Anlegen oder Löschen ist Superadmins vorbehalten.
            </div>
          </div>
        )}
        {/* Stats */}
        <section className="grid grid-cols-2 lg:grid-cols-4 gap-0 border border-ink-200 bg-white rounded-sm mb-8 lg:divide-x lg:divide-ink-200 divide-y lg:divide-y-0">
          <div className="p-6 relative">
            <span className="absolute top-0 left-0 h-0.5 w-full bg-brand-400" />
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-widest font-semibold text-warm-500">
                Hinterlegte KVs
              </div>
              <BookOpenText size={16} weight="duotone" className="text-brand-500" />
            </div>
            <div
              className="font-cabinet text-4xl font-medium text-ink-900 leading-none"
              data-testid="kv-stat-total"
            >
              {stats.total}
            </div>
          </div>
          <div className="p-6">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-widest font-semibold text-warm-500">
                Mit Gleichwertigkeitsprüfung
              </div>
              <CheckCircle size={16} weight="duotone" className="text-status-aktiv-text" />
            </div>
            <div
              className="font-cabinet text-4xl font-medium text-ink-900 leading-none"
              data-testid="kv-stat-equivalence"
            >
              {stats.equivalence}
            </div>
          </div>
          <div className="p-6 lg:col-span-2 bg-warm-50">
            <div className="flex items-start gap-3">
              <Info size={16} weight="bold" className="text-warm-600 mt-0.5 shrink-0" />
              <p className="text-xs text-warm-700 leading-relaxed">
                <strong className="text-ink-900">Hinweis:</strong> In dieser
                Datenbank werden ausschließlich KV-Stammdaten verwaltet. Niemals
                werden hier Endkunden-Daten, Mitarbeiter-Daten oder Datei-Uploads
                gespeichert.
              </p>
            </div>
          </div>
        </section>

        {/* Search + Create */}
        <section className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
          <div className="text-xs uppercase tracking-widest font-semibold text-warm-500">
            {filtered.length} {filtered.length === 1 ? "Eintrag" : "Einträge"}
            {search && ` (gefiltert aus ${items.length})`}
          </div>
          <div className="flex items-center gap-3 w-full lg:w-auto">
            <div className="relative flex-1 lg:flex-none lg:w-80">
              <MagnifyingGlass
                size={14}
                weight="bold"
                className="absolute left-3 top-1/2 -translate-y-1/2 text-warm-500 pointer-events-none"
              />
              <Input
                placeholder="Suche nach Name oder Beschreibung…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="kv-search-input"
              />
            </div>
            {isSuperadmin && (
              <Button
                variant="brand"
                size="md"
                onClick={openCreate}
                data-testid="kv-create-btn"
                className="shrink-0"
              >
                <Plus size={14} weight="bold" />
                Kollektivvertrag anlegen
              </Button>
            )}
          </div>
        </section>

        {/* Table */}
        <section className="border border-ink-200 bg-white rounded-sm overflow-hidden">
          {loading && <div className="loading-bar h-0.5" />}
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-kvs-table">
              <thead>
                <tr className="border-b border-ink-200 bg-warm-50">
                  {[
                    "Kollektivvertrag",
                    "Sichtbarkeit",
                    "Kurzbeschreibung",
                    "Gleichwertigkeit",
                    "Aktualisiert",
                    "Aktionen",
                  ].map((h) => (
                    <th
                      key={h}
                      className="text-left px-5 py-3 text-[10px] uppercase tracking-widest font-semibold text-warm-600"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {!loading && filtered.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-5 py-16 text-center">
                      <BookOpenText
                        size={32}
                        weight="duotone"
                        className="text-warm-400 mx-auto mb-3"
                      />
                      <div className="font-medium text-ink-900 mb-1">
                        {items.length === 0
                          ? "Noch keine Kollektivverträge angelegt"
                          : "Keine Treffer"}
                      </div>
                      <div className="text-xs text-warm-600 mb-5">
                        {items.length === 0
                          ? "Legen Sie den ersten Kollektivvertrag an, um zu starten."
                          : "Bitte passen Sie die Suche an."}
                      </div>
                      {items.length === 0 && isSuperadmin && (
                        <Button
                          variant="brand"
                          size="sm"
                          onClick={openCreate}
                          data-testid="kv-empty-create-btn"
                        >
                          <Plus size={13} weight="bold" />
                          Ersten KV anlegen
                        </Button>
                      )}
                    </td>
                  </tr>
                )}
                {filtered.map((kv) => {
                  const isBusy = busyId === kv.id;
                  return (
                    <tr
                      key={kv.id}
                      className="border-b border-ink-100 last:border-b-0 transition-crisp hover:bg-ink-50 align-top"
                      data-testid={`kv-row-${kv.id}`}
                    >
                      <td className="px-5 py-4 max-w-xs">
                        <div className="font-medium text-ink-900">{kv.name}</div>
                        <div className="text-[10px] uppercase tracking-widest text-warm-500 mt-1">
                          Regelwerk · {kv.ruleset.length.toLocaleString("de-AT")} Zeichen
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        {kv.is_visible === false ? (
                          <div className="space-y-1">
                            <span
                              className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm text-[11px] font-medium border border-warm-300 bg-warm-50 text-warm-700"
                              data-testid={`kv-row-visibility-${kv.id}`}
                            >
                              <EyeSlash size={11} weight="bold" />
                              Ausgeblendet
                            </span>
                            {(kv.beta_user_ids || []).length > 0 && (
                              <div className="text-[10px] uppercase tracking-widest text-brand-700">
                                Beta · {kv.beta_user_ids.length}{" "}
                                {kv.beta_user_ids.length === 1 ? "Anwender" : "Anwender"}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span
                            className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm text-[11px] font-medium border border-status-aktiv-border bg-status-aktiv-bg text-status-aktiv-text"
                            data-testid={`kv-row-visibility-${kv.id}`}
                          >
                            <Eye size={11} weight="bold" />
                            Sichtbar
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-4 text-warm-700 text-sm max-w-md">
                        <div className="line-clamp-3">{kv.short_description}</div>
                      </td>
                      <td className="px-5 py-4">
                        {kv.equivalence_check_required ? (
                          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm text-[11px] font-medium border border-status-aktiv-border bg-status-aktiv-bg text-status-aktiv-text">
                            <CheckCircle size={11} weight="bold" />
                            Erforderlich
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-sm text-[11px] font-medium border border-ink-200 text-warm-600">
                            Nicht erforderlich
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-4 text-xs text-warm-500 whitespace-nowrap">
                        {formatDate(kv.updated_at || kv.created_at)}
                      </td>
                      <td className="px-5 py-4">
                        {isSuperadmin ? (
                          <div className="flex items-center gap-2">
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => openEdit(kv)}
                              disabled={isBusy}
                              data-testid={`kv-edit-btn-${kv.id}`}
                            >
                              <PencilSimple size={13} weight="bold" />
                              Bearbeiten
                            </Button>
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => navigate(`/admin/kvs/${kv.id}/struktur`)}
                              disabled={isBusy}
                              data-testid={`kv-struktur-btn-${kv.id}`}
                              title="Strukturierter Editor (Faktoren, Boni, Sonderregeln)"
                            >
                              Strukturiert
                            </Button>
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => duplicate(kv)}
                              disabled={isBusy}
                              data-testid={`kv-duplicate-btn-${kv.id}`}
                              title="Diesen KV als versteckte Kopie duplizieren"
                            >
                              <CopySimple size={13} weight="bold" />
                              Duplizieren
                            </Button>
                            <button
                              onClick={() => remove(kv)}
                              disabled={isBusy}
                              data-testid={`kv-delete-btn-${kv.id}`}
                              className="inline-flex items-center justify-center h-7 w-7 rounded-sm border border-ink-200 text-ink-400 hover:text-status-abgelehnt-text hover:border-status-abgelehnt-border transition-crisp disabled:opacity-50"
                              aria-label="Kollektivvertrag löschen"
                            >
                              <Trash size={13} weight="bold" />
                            </button>
                          </div>
                        ) : (
                          <span
                            data-testid={`kv-readonly-pill-${kv.id}`}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-medium uppercase tracking-wider border border-ink-200 text-warm-600"
                            title="Nur Superadmins können Kollektivverträge bearbeiten."
                          >
                            <Lock size={10} weight="bold" />
                            Nur Superadmin
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <KvFormModal
        open={modalOpen}
        mode={modalMode}
        initial={editing}
        onClose={() => setModalOpen(false)}
        onSaved={onSaved}
      />
    </AdminLayout>
  );
}
